"use client"

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

const STORAGE_KEY = "vucar-orchestrator-workflows"
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2_000
const STALE_TIMEOUT_MS = 180_000

export type WorkflowStatus = "running" | "completed" | "failed"

export interface WorkflowLog {
  seq: number
  type: string
  content?: string
  tool_name?: string
  tool_input?: any
  tool_result?: string
  tool_status?: string
  ts?: string
}

export interface WorkflowState {
  carId: string
  status: WorkflowStatus
  runId: string | null
  jobId?: string | null
  logs: WorkflowLog[]
  startedAt: string
  retryCount: number
  triggerData?: Record<string, any>
  error?: string
  resultSummary?: string
  agentOutput?: string
}

interface OrchestratorContextValue {
  workflows: Map<string, WorkflowState>
  startWorkflow: (carId: string, data?: Record<string, any>) => Promise<{ success: boolean; error?: string; queued?: boolean; jobId?: string | null }>
  dismissWorkflow: (carId: string) => void
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
}

const OrchestratorContext = createContext<OrchestratorContextValue | null>(null)

export function useOrchestrator() {
  const ctx = useContext(OrchestratorContext)
  if (!ctx) throw new Error("useOrchestrator must be used within OrchestratorProvider")
  return ctx
}

interface StoredWorkflow {
  carId: string
  status: WorkflowStatus
  runId: string | null
  jobId?: string | null
  startedAt: string
  retryCount: number
  triggerData?: Record<string, any>
}

function saveToStorage(workflows: Map<string, WorkflowState>) {
  const stored: StoredWorkflow[] = []
  workflows.forEach((w) => {
    if (w.status === "running") {
      stored.push({
        carId: w.carId,
        status: w.status,
        runId: w.runId,
        jobId: w.jobId,
        startedAt: w.startedAt,
        retryCount: w.retryCount,
        triggerData: w.triggerData,
      })
    }
  })
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {}
}

function loadFromStorage(): StoredWorkflow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as StoredWorkflow[]
  } catch {
    return []
  }
}

export function OrchestratorProvider({ children }: { children: ReactNode }) {
  const [workflows, setWorkflows] = useState<Map<string, WorkflowState>>(new Map())
  const [dialogOpen, setDialogOpen] = useState(false)

  const updateWorkflow = useCallback((carId: string, updater: (prev: WorkflowState) => WorkflowState) => {
    setWorkflows((prev) => {
      const next = new Map(prev)
      const existing = next.get(carId)
      if (!existing) return prev
      next.set(carId, updater(existing))
      saveToStorage(next)
      return next
    })
  }, [])

  const triggerBackend = useCallback(async (
    carId: string,
    data: Record<string, any> | undefined,
    retryCount: number,
  ): Promise<{ success: boolean; error?: string; queued?: boolean; jobId?: string | null }> => {
    try {
      const res = await fetch("/api/e2e/agent-v2/trigger", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ carId, ...(data && Object.keys(data).length > 0 ? { data } : {}) }),
      })
      const result = await res.json()

      if (!result.success) {
        const isSkip = result.action === "skipped"
        if (isSkip) {
          updateWorkflow(carId, (w) => ({
            ...w,
            status: "completed",
            runId: result.run_id || null,
            error: `Skipped: ${result.reason}`,
          }))
          return { success: false, error: `Skipped: ${result.reason}` }
        }

        if (retryCount < MAX_RETRIES) {
          updateWorkflow(carId, (w) => ({ ...w, retryCount: retryCount + 1 }))
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
          return triggerBackend(carId, data, retryCount + 1)
        }

        updateWorkflow(carId, (w) => ({
          ...w,
          status: "failed",
          runId: result.run_id || null,
          error: result.error || "Trigger thất bại sau 3 lần thử",
        }))
        return { success: false, error: result.error || "Trigger failed" }
      }

      updateWorkflow(carId, (w) => ({
        ...w,
        runId: result.run_id ?? w.runId,
        jobId: result.job_id ?? w.jobId,
        resultSummary: result.queued ? "Đã đưa vào hàng chờ xử lý" : w.resultSummary,
      }))

      return { success: true, queued: !!result.queued, jobId: result.job_id || null }
    } catch (err) {
      if (retryCount < MAX_RETRIES) {
        updateWorkflow(carId, (w) => ({ ...w, retryCount: retryCount + 1 }))
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
        return triggerBackend(carId, data, retryCount + 1)
      }

      updateWorkflow(carId, (w) => ({
        ...w,
        status: "failed",
        error: "Không thể kết nối sau 3 lần thử",
      }))
      return { success: false, error: err instanceof Error ? err.message : "Network error" }
    }
  }, [updateWorkflow])

  const startWorkflow = useCallback(async (carId: string, data?: Record<string, any>): Promise<{ success: boolean; error?: string; queued?: boolean; jobId?: string | null }> => {
    if (workflows.has(carId) && workflows.get(carId)!.status === "running") {
      return { success: false, error: "Workflow already running for this car" }
    }

    const newWorkflow: WorkflowState = {
      carId,
      status: "running",
      runId: null,
      jobId: null,
      logs: [],
      startedAt: new Date().toISOString(),
      retryCount: 0,
      triggerData: data,
    }

    setWorkflows((prev) => {
      const next = new Map(prev)
      next.set(carId, newWorkflow)
      saveToStorage(next)
      return next
    })

    return triggerBackend(carId, data, 0)
  }, [workflows, triggerBackend])

  const dismissWorkflow = useCallback((carId: string) => {
    setWorkflows((prev) => {
      const next = new Map(prev)
      next.delete(carId)
      saveToStorage(next)
      return next
    })
  }, [])

  useEffect(() => {
    const stored = loadFromStorage()
    if (!stored.length) return
    setWorkflows((prev) => {
      const next = new Map(prev)
      for (const s of stored) {
        if (!next.has(s.carId)) {
          const ageMs = Date.now() - new Date(s.startedAt).getTime()
          if (ageMs > STALE_TIMEOUT_MS && !s.runId && !s.jobId) {
            continue
          }
          next.set(s.carId, {
            carId: s.carId,
            status: s.status,
            runId: s.runId,
            jobId: s.jobId,
            logs: [],
            startedAt: s.startedAt,
            retryCount: s.retryCount || 0,
            triggerData: s.triggerData,
          })
        }
      }
      return next
    })
  }, [])

  useEffect(() => {
    const runningCarIds = Array.from(workflows.entries())
      .filter(([, w]) => w.status === "running")
      .map(([carId]) => carId)
    if (runningCarIds.length === 0) return

    let cancelled = false

    const poll = async () => {
      for (const carId of runningCarIds) {
        if (cancelled) return
        const wf = workflows.get(carId)
        if (!wf) continue

        const ageMs = Date.now() - new Date(wf.startedAt).getTime()

        if (ageMs > STALE_TIMEOUT_MS && !wf.runId) {
          // Queue-backed triggers may wait behind the worker backlog before a
          // run row exists. Do not mark them failed/retry just because run_id is
          // not available yet; keep polling until the worker creates a run.
          if (wf.jobId) {
            continue
          }
          if (wf.retryCount < MAX_RETRIES) {
            const nextRetry = wf.retryCount + 1
            updateWorkflow(carId, (w) => ({
              ...w,
              startedAt: new Date().toISOString(),
              retryCount: nextRetry,
            }))
            await triggerBackend(carId, wf.triggerData, nextRetry)
          } else {
            updateWorkflow(carId, (w) => ({
              ...w,
              status: "failed",
              error: "Hết thời gian chờ sau 3 lần thử",
            }))
          }
          continue
        }

        try {
          const res = await fetch(`/api/e2e/agent-v2/runs/${encodeURIComponent(carId)}`)
          if (!res.ok) continue
          const data = await res.json()
          const runs: Array<{
            run_id: string
            status: string
            error: string | null
            created_at: string | null
            completed_at: string | null
          }> = data.runs || []
          if (runs.length === 0) continue

          const startedMs = new Date(wf.startedAt).getTime()
          const matched = runs
            .filter((r) => {
              if (!r.created_at) return false
              return new Date(r.created_at).getTime() >= startedMs - 5_000
            })
            .sort((a, b) =>
              new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime(),
            )[0]
          if (!matched) continue

          if (matched.status === "completed") {
            updateWorkflow(carId, (w) => ({
              ...w,
              status: "completed",
              runId: matched.run_id,
            }))
          } else if (matched.status === "failed" || matched.status === "error") {
            updateWorkflow(carId, (w) => ({
              ...w,
              status: "failed",
              runId: matched.run_id,
              error: matched.error || "Run failed",
            }))
          } else if (matched.status === "running" && wf.runId !== matched.run_id) {
            updateWorkflow(carId, (w) => ({
              ...w,
              runId: matched.run_id,
            }))
          }
        } catch {
          /* non-fatal */
        }
      }
    }

    poll()
    const interval = setInterval(poll, 5_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [workflows, updateWorkflow, triggerBackend])

  return (
    <OrchestratorContext.Provider value={{ workflows, startWorkflow, dismissWorkflow, dialogOpen, setDialogOpen }}>
      {children}
    </OrchestratorContext.Provider>
  )
}
